import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ContactFilterCriteria } from "./contacts-types";
import { buildContactFilterQuery, getFilteredContactIds } from "./contacts-bulk-filter-utils";
import { getSupabase } from "@/lib/hooks/api/utils";

interface ContactRow {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  status: string | null;
  contact_type: string | null;
  is_active: boolean | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useBulkExportContactsByFilter() {
  return useMutation({
    mutationFn: async ({ filterCriteria }: { filterCriteria: ContactFilterCriteria }) => {
      const supabase = getSupabase();
      const query = buildContactFilterQuery(supabase, filterCriteria);

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching contacts for export:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No contacts found matching the current filters");
      }

      const { objectsToCSV, downloadCSV, formatDateForCSV, formatBooleanForCSV } =
        await import("@/lib/utils/csv-export");

      const exportData = data.map((contact) => ({
        ID: contact.id,
        Name: contact.name || "",
        Email: contact.email || "",
        Phone: contact.phone || "",
        "Company Name": contact.company_name || "",
        Status: contact.status || "",
        "Contact Type": contact.contact_type || "",
        "Is Active": formatBooleanForCSV(contact.is_active),
        Address: contact.address || "",
        City: contact.city || "",
        State: contact.state || "",
        "Zip Code": contact.zip_code || "",
        Country: contact.country || "",
        Notes: contact.notes || "",
        "Created At": contact.created_at ? formatDateForCSV(contact.created_at) : "",
        "Updated At": contact.updated_at ? formatDateForCSV(contact.updated_at) : "",
      }));

      const csvContent = objectsToCSV(exportData);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `contacts_export_${timestamp}.csv`);

      return { exported: data.length };
    },
    onSuccess: (result) => {
      toast.success(`${result.exported} contacts exported successfully`);
    },
    onError: (error) => {
      toast.error("Failed to export contacts");
      console.error("Error exporting contacts:", error);
    },
  });
}

export function useBulkEmailContactsByFilter() {
  return useMutation({
    mutationFn: async ({ filterCriteria }: { filterCriteria: ContactFilterCriteria }) => {
      const contacts = await getFilteredContactIds(filterCriteria);

      if (contacts.length === 0) {
        throw new Error("No contacts found matching the current filters");
      }

      const contactsWithEmail = contacts.filter((contact) => contact.email);

      if (contactsWithEmail.length === 0) {
        throw new Error("No contacts with email addresses found matching the current filters");
      }

      return {
        contacts: contactsWithEmail,
        totalCount: contactsWithEmail.length,
        emailList: contactsWithEmail.map((c) => c.email).join(", "),
      };
    },
    onSuccess: (result) => {
      toast.success(`Found ${result.totalCount} contacts with email addresses`);
    },
    onError: (error) => {
      toast.error("Failed to get contacts for email");
      console.error("Error getting contacts for bulk email:", error);
    },
  });
}

export function useBulkExportContactsEmailsByFilter() {
  return useMutation({
    mutationFn: async ({ filterCriteria }: { filterCriteria: ContactFilterCriteria }) => {
      const contacts = await getFilteredContactIds(filterCriteria);

      if (contacts.length === 0) {
        throw new Error("No contacts found matching the current filters");
      }

      const contactsWithEmail = contacts.filter((contact) => contact.email);

      if (contactsWithEmail.length === 0) {
        throw new Error("No contacts with email addresses found matching the current filters");
      }

      const emailList = contactsWithEmail.map((c) => c.email).join("; ");

      return {
        emails: contactsWithEmail.map((c) => c.email),
        emailList,
        totalCount: contactsWithEmail.length,
      };
    },
    onSuccess: (result) => {
      toast.success(`Found ${result.totalCount} contacts with email addresses`);
    },
    onError: (error) => {
      toast.error("Failed to export contact emails");
      console.error("Error exporting contact emails by filter:", error);
    },
  });
}
